<script setup>
import { ref, getCurrentInstance } from 'vue'
import { useForm, router } from '@inertiajs/vue3'
import { useLoading } from '@/composables/useLoading'

const { proxy } = getCurrentInstance()
const $t = proxy.$t
const { showLoading, hideLoading } = useLoading()

const props = defineProps({
    user: Object,
})

const form = useForm({
    _method: 'PUT',
    first_name: props.user?.first_name || '',
    last_name: props.user?.last_name || '',
    name: props.user?.name || '',
    birthday: props.user?.birthday || '',
    email: props.user?.email || '',
    photo: null,
    phone: props.user?.phone || '',
})

const verificationLinkSent = ref(false)

const updateProfileInformation = () => {
    showLoading($t('Updating email information...'));
    const routeName = proxy.route ? proxy.route('client.profile-information.update') : '/profile-information'
    form.post(routeName, {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onFinish: () => hideLoading(),
    })
}

const sendEmailVerification = () => {
    showLoading($t('Sending verification email...'));
    const routeName = proxy.route ? proxy.route('verification.send') : '/email/verify'
    router.post(routeName, {}, {
        preserveScroll: true,
        onSuccess: () => {
            verificationLinkSent.value = true
        },
        onFinish: () => hideLoading(),
    })
}
</script>

<template>
    <form @submit.prevent="updateProfileInformation" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Email -->
            <div class="field">
                <label for="email" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Email') }}
                </label>
                <InputText
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.email }"
                    autocomplete="email"
                />
                <small v-if="form.errors.email" class="p-error">
                    {{ form.errors.email }}
                </small>

                <!-- Email Verification -->
                <div
                    v-if="proxy.$page.props.jetstream?.hasEmailVerification && props.user?.email_verified_at === null"
                    class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                >
                    <div class="flex items-start gap-2">
                        <i class="pi pi-exclamation-triangle text-yellow-600 mt-0.5"></i>
                        <div>
                            <p class="text-sm text-yellow-800 mb-2">
                                {{ $t('Your email address is unverified.') }}
                            </p>
                            <Button
                                type="button"
                                :label="$t('Click here to re-send the verification email')"
                                link
                                size="small"
                                class="p-0 text-yellow-700 hover:text-yellow-900"
                                @click="sendEmailVerification"
                            />
                        </div>
                    </div>

                    <Message
                        v-if="verificationLinkSent"
                        severity="success"
                        :closable="false"
                        class="mt-2"
                    >
                        {{ $t('A new verification link has been sent to your email address.') }}
                    </Message>
                </div>
            </div>

            <!-- Phone -->
            <div class="field">
                <label for="phone" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Phone') }}
                </label>
                <InputText
                    id="phone"
                    v-model="form.phone"
                    type="tel"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.phone }"
                    autocomplete="tel"
                />
                <small v-if="form.errors.phone" class="p-error">
                    {{ form.errors.phone }}
                </small>
            </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-4 pt-4">
            <Button
                type="submit"
                :label="form.processing ? $t('Saving...') : $t('Save')"
                icon="pi pi-save"
                :loading="form.processing"
                :disabled="form.processing"
            />

            <div v-if="form.recentlySuccessful" class="flex items-center gap-2 text-green-600">
                <i class="pi pi-check-circle"></i>
                <span class="text-sm font-medium">{{ $t('Saved.') }}</span>
            </div>
        </div>
    </form>
</template>

<style scoped>
.field {
    @apply flex flex-col;
}

.p-error {
    @apply text-red-500 text-xs mt-1;
}

.p-invalid {
    @apply border-red-500;
}
</style>
